// Household service
// - Provide helpers to read and modify household records and membership
// - Encapsulate Supabase queries for households and household_members
// - Return domain-friendly types and handle basic error logging

import { supabase } from "../lib/supabase";

// On mobile PWA resume, Supabase may be mid-token-refresh and getSession()
// can transiently return null. Fall back to getUser() (network-validated) so
// we always resolve a userId when a valid auth token exists.
async function getAuthUserId(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return (
    session?.user?.id ?? (await supabase.auth.getUser()).data.user?.id ?? null
  );
}

export interface Household {
  id: string;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface HouseholdMember {
  id: string;
  household_id: string;
  user_id: string;
  role: "admin" | "member";
  joined_at: string;
  email?: string;
}

// Get the household associated with the currently authenticated user.
// When `householdId` is already known it is fetched directly, avoiding the
// household_members join and the extra getUser() round-trip entirely.
// Returns `Household` or `null` when no user or household found.
export async function getUserHousehold(
  householdId?: string,
): Promise<Household | null> {
  if (householdId) {
    const { data, error } = await supabase
      .from("households")
      .select("id, name, created_by, created_at, updated_at")
      .eq("id", householdId)
      .single();
    if (error || !data) return null;
    return data as Household;
  }

  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) return null;

  const { data, error } = await supabase
    .from("household_members")
    .select(
      "household_id, households(id, name, created_by, created_at, updated_at)",
    )
    .eq("user_id", session.user.id)
    .limit(1);

  if (error || !data || data.length === 0) return null;
  return data[0].households as unknown as Household;
}

// Get all households the currently authenticated user belongs to
// Returns an array of Household objects with the user's role in each
export interface HouseholdWithRole extends Household {
  role: "admin" | "member";
}

export async function getUserHouseholds(): Promise<HouseholdWithRole[]> {
  const userId = await getAuthUserId();
  if (!userId) return [];

  const { data, error } = await supabase
    .from("household_members")
    .select("role, households(id, name, created_by, created_at, updated_at)")
    .eq("user_id", userId)
    .order("joined_at", { ascending: true });

  if (error || !data) {
    console.error("🔴 Error fetching user households:", error);
    return [];
  }

  return data
    .map((row) => {
      const h = Array.isArray(row.households)
        ? row.households[0]
        : row.households;
      if (!h) return null;
      return { ...(h as Household), role: row.role as "admin" | "member" };
    })
    .filter((h): h is HouseholdWithRole => h !== null);
}

// Get the household id for the current session user
// Returns household id string or null when not available
export async function getUserHouseholdId(
  signal?: AbortSignal,
): Promise<string | null> {
  const userId = await getAuthUserId();
  if (!userId) return null;

  const query = supabase
    .from("household_members")
    .select("household_id")
    .eq("user_id", userId)
    .limit(1);

  if (signal) {
    query.abortSignal(signal);
  }

  const { data, error } = await query;

  if (error || !data || data.length === 0) return null;
  return data[0].household_id;
}

// Internal DB shape used when joined with profiles
interface HouseholdMemberWithProfile {
  id: string;
  household_id: string;
  user_id: string;
  role: "admin" | "member";
  joined_at: string;
  profiles: {
    email: string;
  } | null;
}

// Fetch members for a household, include email when available
// Returns an array of `HouseholdMember`
export async function getHouseholdMembers(
  householdId: string,
): Promise<HouseholdMember[]> {
  const { data, error } = await supabase
    .from("household_members")
    .select(
      `
      id,
      household_id,
      user_id,
      role,
      joined_at,
      profiles (
        email
      )
    `,
    )
    .eq("household_id", householdId)
    .order("joined_at", { ascending: true });

  if (error) {
    console.error("🔴 Error fetching household members:", error);
    return [];
  }

  const members = data as unknown as HouseholdMemberWithProfile[];

  return (members || []).map((member) => ({
    id: member.id,
    household_id: member.household_id,
    user_id: member.user_id,
    role: member.role,
    joined_at: member.joined_at,
    email: member.profiles?.email,
  }));
}

// Create a new household record for a given user id
// Returns created `Household` or null on failure
export async function createHousehold(
  name: string,
  userId: string,
): Promise<Household | null> {
  const { data: household, error: householdError } = await supabase
    .from("households")
    .insert({
      name,
      created_by: userId,
    })
    .select()
    .single();

  if (householdError) {
    console.error("🔴 Error creating household:", householdError);
    return null;
  }

  return household as Household;
}

// Update household name by id
// Returns boolean indicating success
export async function updateHouseholdName(
  householdId: string,
  name: string,
): Promise<boolean> {
  const { error } = await supabase
    .from("households")
    .update({ name, updated_at: new Date().toISOString() })
    .eq("id", householdId);

  if (error) {
    console.error("🔴 Error updating household:", error);
    return false;
  }

  return true;
}

// Remove a household member by membership id
// Returns boolean indicating success
export async function removeMember(memberId: string): Promise<boolean> {
  const { error } = await supabase
    .from("household_members")
    .delete()
    .eq("id", memberId);

  if (error) {
    console.error("🔴 Error removing member:", error);
    return false;
  }

  return true;
}

// Check whether a user is an admin for a given household id
// Returns boolean
export async function isUserHouseholdAdmin(
  userId: string,
  householdId: string,
): Promise<boolean> {
  const { data, error } = await supabase
    .from("household_members")
    .select("role")
    .eq("user_id", userId)
    .eq("household_id", householdId)
    .maybeSingle();

  if (error || !data) return false;
  return data.role === "admin";
}

// Delete a household by id
// Returns boolean indicating success
export async function deleteHousehold(householdId: string): Promise<boolean> {
  const { error } = await supabase
    .from("households")
    .delete()
    .eq("id", householdId);

  if (error) {
    console.error("🔴 Error deleting household:", error);
    return false;
  }

  return true;
}

// Delete the currently authenticated user and remove them from households
// Performs member cleanup then calls a DB RPC to delete the auth user
// Returns boolean indicating overall success
export async function deleteCurrentUser(): Promise<boolean> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;

    // First, remove the user from any households
    const { error: memberError } = await supabase
      .from("household_members")
      .delete()
      .eq("user_id", user.id);

    if (memberError) {
      console.error("🔴 Error removing user from households:", memberError);
      return false;
    }

    // Delete the user's auth account
    // Note: This requires the user to be authenticated
    const { error: deleteError } = await supabase.rpc("delete_user");

    if (deleteError) {
      console.error("🔴 Error deleting user account:", deleteError);
      return false;
    }

    return true;
  } catch (error) {
    console.error("🔴 Error in deleteCurrentUser:", error);
    return false;
  }
}
